import { useAuth } from '../../context/AuthContext';
import browseAreaHome from '../../lib/browseAreaHome';
import { Button } from '../../components/ui/button';
import { Select } from '../../components/ui/select';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from '../../lib/api';
import Seo from '../../components/Seo';
import { ArrowRight, MapPin, RotateCcw, RefreshCw } from 'lucide-react';

import loadingLogo from '../../assets/og-image.png';
import loadingStyles from '../../styles/HomeLoading.module.css';

// Client navigation uses the same escaped HTML and metadata as the public
// Express renderer. Direct production requests are routed to that renderer.
export default function LocalDiscovery() {
  const { pathname, search = '' } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectingArea, setSelectingArea] = useState(false);
  const [areaError, setAreaError] = useState('');
  const storeType = new URLSearchParams(search).get('storeType') || '';
  const requestPath = pathname + search;
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const [stylesReady, setStylesReady] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    get('/seo/page?' + new URLSearchParams({ path: pathname.replace(/\/$/, ''), ...(storeType ? { storeType } : {}) }), { signal: controller.signal })
      .then(page => {
        if (controller.signal.aborted) return;
        if (page.path && page.path !== pathname) {
          navigate(page.path + (storeType ? '?' + new URLSearchParams({ storeType }) : ''), { replace: true });
        }
        setResult({ page, path: requestPath });
      })
      .catch(error => { if (!controller.signal.aborted) setResult({ error: error.response?.status === 404 ? 'Page not found.' : 'The public directory is temporarily unavailable. Please try again.', path: requestPath }); });
    return () => controller.abort();
  }, [pathname, storeType, requestPath, retry, navigate]);
  if (!result || result.path !== requestPath) return <>
    <Seo title="Loading local food shops | BigCircle PH" robots="noindex, follow" />
    <DirectoryLoader />
  </>;
  if (result.error) return <>
    <Seo title="Public directory unavailable | BigCircle PH" robots="noindex, follow" />
    <section className="flex min-h-80 flex-col items-center justify-center rounded-3xl border border-[#e0e6d9] bg-[#faf8f1] px-6 py-12 text-center">
      <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eaf0e2] text-[#397548]"><MapPin className="h-7 w-7" aria-hidden="true" /></span>
      <h1 className="text-2xl font-bold tracking-tight text-[#203c2c]">Let’s get you back to local favorites</h1>
      <p role="alert" className="mt-3 max-w-md text-sm leading-7 text-[#637164]">{result.error}</p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <button type="button" onClick={() => setRetry(value => value + 1)} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#173f2a] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#285838] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#397548]"><RotateCcw className="h-4 w-4" aria-hidden="true" />Try again</button>
        <a href="/food-delivery" className="inline-flex min-h-12 items-center gap-2 rounded-xl border border-[#d8e1cc] bg-white px-5 py-3 text-sm font-semibold text-[#397548] transition hover:bg-[#edf3e8]">Browse Delivery Areas<ArrowRight className="h-4 w-4" aria-hidden="true" /></a>
      </div>
    </section>
  </>;
  const { page } = result;
  const filterMarkup = /<form\b[^>]*data-store-filter[^>]*>[\s\S]*?<\/form>/.exec(page.html);
  const enhancedFilter = page.storeFilter && filterMarkup;
  const beforeFilter = enhancedFilter ? page.html.slice(0, filterMarkup.index) : '';
  const afterFilter = enhancedFilter ? page.html.slice(filterMarkup.index + filterMarkup[0].length) : '';
  const stylesheet = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '') + '/seo/styles.css?v=15';
  return <>
    <Seo title={page.title} description={page.description} path={page.path} robots={page.robots} image={page.image} structuredData={page.structuredData} />
    <link rel="stylesheet" href={stylesheet} crossOrigin="anonymous" onLoad={() => setStylesReady(true)} onError={() => setResult({ error: 'The public directory styles could not load. Please try again.', path: requestPath })} />
    {!stylesReady && <DirectoryLoader />}
    {areaError && <p role="alert" className="mb-3 text-sm text-red-700">{areaError}</p>}
    <div onClick={async event => {
      const button = event.target.closest('[data-browse-area]');
      if (!button || !page.browseArea) return;
      event.preventDefault();
      if (selectingArea) return;
      setSelectingArea(true); setAreaError('');
      button.setAttribute('aria-disabled', 'true');
      try {
        await browseAreaHome(page.browseArea, user);
      } catch (error) {
        setAreaError(error.response?.data?.message || 'Unable to change service area. Please try again.');
      } finally {
        setSelectingArea(false); button.removeAttribute('aria-disabled');
      }
    }} hidden={!stylesReady} className={'seo-document' + (pathname.replace(/\/$/, '') !== '/food-delivery' ? ' delivery-area-document' : ' discovery-directory')} onSubmit={event => {
      if (!event.target.matches('form[data-store-filter]')) return;
      event.preventDefault();
      const value = new FormData(event.target).get('storeType');
      navigate(page.path + (value ? '?' + new URLSearchParams({ storeType: value }) : ''));
    }}><div className="discovery-shell">{enhancedFilter ? <>
      <div dangerouslySetInnerHTML={{ __html: beforeFilter }} />
      <form className="discovery-store-filter" data-store-filter method="get" action={page.path}>
        <Select key={page.path + page.storeFilter.value} name="storeType" aria-label="Store type" defaultValue={page.storeFilter.value}>
          <option value="all">All store types</option>
          {page.storeFilter.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
        <Button type="submit" variant="secondary" size="icon" aria-label="Load stores" title="Load stores"><RefreshCw className="h-5 w-5" aria-hidden="true" /></Button>
      </form>
      <div dangerouslySetInnerHTML={{ __html: afterFilter }} />
    </> : <div dangerouslySetInnerHTML={{ __html: page.html }} />}</div></div>
  </>;
}

function DirectoryLoader() {
  return <div role="status" aria-busy="true" className={loadingStyles.container}>
    <div aria-hidden="true" className={loadingStyles.mark}>
      <img src={loadingLogo} alt="BigCircle" width="96" height="96" className={loadingStyles.logo} />
      <span className={loadingStyles.shadow} />
    </div>
    <span className="sr-only">Loading stores...</span>
  </div>;
}
